/**
 * External dependencies
 */
import clsx from 'clsx';
/**
 * WordPress dependencies
 */
import { useInstanceId, useMergeRefs } from '@wordpress/compose';
import { __ } from '@wordpress/i18n';
import { search, closeSmall } from '@wordpress/icons';
import { forwardRef, useRef } from '@wordpress/element';
import deprecated from '@wordpress/deprecated';

/**
 * Internal dependencies
 */
import Button from '../button';
import { InputControlPrefixWrapper } from '../input-control/input-prefix-wrapper';
import { InputControlSuffixWrapper } from '../input-control/input-suffix-wrapper';
import { StyledInputControl, StyledIcon } from './styles';
import { jsx as _jsx } from "react/jsx-runtime";
function SuffixItem({
  searchRef,
  value,
  onChange,
  onClose
}) {
  if (!onClose && !value) {
    return null;
  }
  if (onClose) {
    deprecated('`onClose` prop in wp.components.SearchControl', {
      since: '6.8'
    });
  }
  const onReset = () => {
    onChange('');
    searchRef.current?.focus();
  };
  return /*#__PURE__*/_jsx(InputControlSuffixWrapper, {
    variant: "control",
    children: /*#__PURE__*/_jsx(Button, {
      size: "small",
      icon: closeSmall,
      label: onClose ? __('Close search') : __('Reset search'),
      onClick: onClose ?? onReset
    })
  });
}
function UnforwardedSearchControl({
  __nextHasNoMarginBottom: _,
  // Prevent passing to internal component
  className,
  onChange,
  value,
  label = __('Search'),
  placeholder = __('Search'),
  hideLabelFromVision = true,
  onClose,
  size = 'default',
  ...restProps
}, forwardedRef) {
  // @ts-expect-error The `disabled` prop is not yet supported in the SearchControl component.
  // Work with the design team (@WordPress/gutenberg-design) if you need this feature.
  const {
    disabled,
    ...filteredRestProps
  } = restProps;
  const searchRef = useRef(null);
  const instanceId = useInstanceId(SearchControl, 'components-search-control');
  return /*#__PURE__*/_jsx(StyledInputControl, {
    __next40pxDefaultSize: true,
    id: instanceId,
    hideLabelFromVision: hideLabelFromVision,
    label: label,
    ref: useMergeRefs([searchRef, forwardedRef]),
    type: "search",
    size: size,
    className: clsx('components-search-control', className),
    onChange: nextValue => onChange(nextValue ?? ''),
    autoComplete: "off",
    placeholder: placeholder,
    value: value ?? '',
    prefix: /*#__PURE__*/_jsx(InputControlPrefixWrapper, {
      variant: "icon",
      children: /*#__PURE__*/_jsx(StyledIcon, {
        icon: search,
        fill: "currentColor"
      })
    }),
    suffix: /*#__PURE__*/_jsx(SuffixItem, {
      searchRef: searchRef,
      value: value,
      onChange: onChange,
      onClose: onClose
    }),
    ...filteredRestProps
  });
}

/**
 * SearchControl components let users display a search control.
 *
 * ```jsx
 * import { SearchControl } from '@wordpress/components';
 * import { useState } from '@wordpress/element';
 *
 * function MySearchControl( { className, setState } ) {
 *   const [ searchInput, setSearchInput ] = useState( '' );
 *
 *   return (
 *     <SearchControl
 *       value={ searchInput }
 *       onChange={ setSearchInput }
 *     />
 *   );
 * }
 * ```
 */
export const SearchControl = forwardRef(UnforwardedSearchControl);
SearchControl.displayName = 'SearchControl';
export default SearchControl;